// Modelo central de permisos por ROL de usuario.
//
// Distinto de las capabilities por TENANT (capabilities.ts): aquello gatea qué
// MÓDULOS tiene contratado un cliente; esto gatea qué puede hacer un USUARIO
// según su rol dentro de ese cliente. El gating efectivo es la intersección:
// el tenant debe tener la capability Y el rol debe tener el permiso.
//
// Roles:
//   - superadmin: Daniel y equipo. Opera todos los clientes. Todo permitido.
//   - client_admin: dueño/admin de un cliente. Opera su CRM, el equipo y el
//     contenido del sitio. La configuración la edita Life Focus.
//   - sales_rep: vendedor operativo. Trabaja el pipeline (leads/contactos/
//     tareas/mensajes: crear, editar, responder) pero NO administra: sin CMS,
//     sin configuración del tenant, sin editar pipelines, sin borrar registros,
//     sin gestión de equipo.
//
// El frontend lee ESTO para ocultar/deshabilitar UI. El backend (PB rules)
// enforcea lo mismo de forma no-bypasseable (migración 1777200033).

export type Role = 'superadmin' | 'client_admin' | 'sales_rep';

export interface RolePermissions {
  /** Ver y editar el contenido del sitio (cms_content). Schemas siguen en superadmin. */
  cms: boolean;
  /** Ajustes del tenant que sigue usando el código interno. La agenda ya no depende de esto. */
  editTenantSettings: boolean;
  /** Borrar registros del CRM (leads, contactos). */
  deleteRecords: boolean;
  /** Invitar/gestionar usuarios del tenant. */
  manageTeam: boolean;
}

// Operaciones del día a día del CRM (crear/editar leads, contactos, tareas,
// notas; responder mensajes) están permitidas para TODOS los roles con acceso
// al tenant — no se modelan como flags acá, son el "piso" operativo.
const PERMISSIONS: Record<Role, RolePermissions> = {
  superadmin: {
    cms: true,
    editTenantSettings: true,
    deleteRecords: true,
    manageTeam: true,
  },
  client_admin: {
    cms: true,
    editTenantSettings: true,
    deleteRecords: true,
    manageTeam: true, // gestiona el equipo de SU tenant (no superadmins ni otros tenants)
  },
  sales_rep: {
    cms: false,
    editTenantSettings: false,
    deleteRecords: false,
    manageTeam: false,
  },
};

// Etiquetas y descripciones de alcance por rol — para mostrar al cliente
// cuando crea un usuario, así entiende qué puede hacer cada rol. Solo los
// roles asignables a un usuario de cliente (no superadmin).
export const ROLE_LABELS: Record<'client_admin' | 'sales_rep', string> = {
  client_admin: 'Administrador',
  sales_rep: 'Vendedor',
};

export const ROLE_DESCRIPTIONS: Record<'client_admin' | 'sales_rep', string> = {
  client_admin:
    'Opera el día a día: oportunidades, contactos, tareas, mensajes, estadísticas y su equipo. Puede desactivar y reactivar usuarios de su cliente. Edita el contenido del sitio. La configuración la hace Life Focus.',
  sales_rep:
    'Trabaja el día a día: crea y edita oportunidades, contactos y tareas, y responde mensajes. Ve la agenda y puede conectar su Google Calendar. No administra el equipo, no edita el sitio ni la configuración, y no borra registros.',
};

/** ¿El rol tiene este permiso? */
export function can(role: Role | string | null | undefined, perm: keyof RolePermissions): boolean {
  if (!role) return false;
  const p = PERMISSIONS[role as Role];
  return p ? p[perm] : false;
}

/** Atajo: rol operativo (puede trabajar el CRM día a día). Todos los roles del
 *  tenant pueden; superadmin también. Se mantiene como función por claridad
 *  semántica en los call sites. */
export function canOperateCrm(role: Role | string | null | undefined): boolean {
  return role === 'superadmin' || role === 'client_admin' || role === 'sales_rep';
}
